import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AwayGuard from '../../components/security/AwayGuard';
import { useTabLabel } from '../../components/shell/TabsContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import RichHtml from '../../components/richtext/RichText';
import AnswerReview from '../../components/questions/AnswerReview';
import AttemptsTable, { useAttempts } from '../../components/exams/AttemptsTable';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  XCircle,
  Warning,
  PaperPlaneTilt,
  ListNumbers,
  FloppyDisk
} from '@phosphor-icons/react';

export default function ExamTaker() {
  const { api } = useAuth();
  const { examId } = useParams();
  const location = useLocation();
  const backTo = location.state?.courseId ? `/learn/course/${location.state.courseId}` : '/learn';
  const [loadError, setLoadError] = useState(null);
  const navigate = useNavigate();
  
  const [exam, setExam] = useState(null);
  useTabLabel(exam?.title ? `Exam: ${exam.title}` : 'Exam');
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [result, setResult] = useState(null);
  const attemptsApi = useAttempts(api, result ? examId : null);
  
  const autoSaveRef = useRef(null);
  const timerRef = useRef(null);

  // Fetch exam details
  const fetchExam = async () => {
    try {
      const res = await api.get(`/exams/${examId}`);
      setExam(res.data);
    } catch (error) {
      setLoadError(error.response?.data?.detail || 'Exam not found');
      setLoading(false);
    }
  };

  // Start or resume attempt
  const startAttempt = async () => {
    try {
      const res = await api.post(`/exams/${examId}/start`);
      setAttempt(res.data);
      setQuestions(res.data.questions || []);
      
      if (res.data.time_limit_seconds) {
        // Calculate remaining time if resuming
        const started = new Date(res.data.started_at);
        const elapsed = Math.floor((new Date() - started) / 1000);
        const remaining = Math.max(0, res.data.time_limit_seconds - elapsed);
        setTimeRemaining(remaining);
      }
      
      // Initialize answers from saved data if resuming
      if (res.data.saved_answers) {
        const savedAnswers = {};
        res.data.saved_answers.forEach(a => {
          savedAnswers[a.question_id] = a;
        });
        setAnswers(savedAnswers);
      }
    } catch (error) {
      setLoadError(error.response?.data?.detail || 'Failed to start exam');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExam();
    startAttempt();
    
    return () => {
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [examId]);

  // Timer countdown
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) return;
    
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [timeRemaining !== null]);

  // Auto-save every 30 seconds
  useEffect(() => {
    if (!attempt) return;
    
    autoSaveRef.current = setInterval(() => {
      saveProgress(true);
    }, 30000);

    return () => clearInterval(autoSaveRef.current);
  }, [attempt, answers]);

  const saveProgress = async (silent = false) => {
    if (!attempt) return;
    
    try {
      const answersArray = Object.entries(answers).map(([qId, answer]) => ({
        question_id: qId,
        ...answer
      }));
      
      await api.post(`/exams/${examId}/attempts/${attempt.attempt_id}/save`, {
        answers: answersArray,
        current_question_index: currentIndex
      });
      
      if (!silent) toast.success('Progress saved');
    } catch (error) {
      if (!silent) toast.error('Failed to save progress');
    }
  };

  const handleAutoSubmit = async () => {
    toast.warning('Time is up! Submitting your exam...');
    await handleSubmit();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const answersArray = Object.entries(answers).map(([qId, answer]) => ({
        question_id: qId,
        ...answer
      }));
      
      const res = await api.post(`/exams/${examId}/attempts/${attempt.attempt_id}/submit`, {
        answers: answersArray
      });
      
      setResult(res.data);
      clearInterval(timerRef.current);
      clearInterval(autoSaveRef.current);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit exam');
    } finally {
      setSubmitting(false);
      setShowSubmitDialog(false);
    }
  };

  /**
   * The student left the screen and the tenant's rules end the session.
   *
   * Submit FIRST and await it: once logout() runs there is no token, and the attempt
   * would be left in progress — the student would keep the attempt and lose nothing,
   * which is the opposite of what this is for. Graded on whatever they had answered.
   */
  const handInOnSignOut = useCallback(async () => {
    if (!attempt?.attempt_id || result) return null;
    const answersArray = Object.entries(answers).map(([qId, answer]) => ({ question_id: qId, ...answer }));
    try {
      await api.post(`/exams/${examId}/attempts/${attempt.attempt_id}/submit`, {
        answers: answersArray,
        auto_submitted: true,
        auto_submit_reason: 'left_the_screen',
      });
      clearInterval(timerRef.current);
      clearInterval(autoSaveRef.current);
      return 'This attempt was submitted with the answers you had given, and it counts towards your allowed attempts.';
    } catch {
      return 'We could not submit this attempt automatically — tell your teacher.';
    }
  }, [api, examId, attempt, answers, result]);

  const updateAnswer = (questionId, answerData) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ...answerData,
        time_spent_seconds: (prev[questionId]?.time_spent_seconds || 0) + 1
      }
    }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).filter(qId => {
      const a = answers[qId];
      return a.answer_text || a.selected_options?.length > 0 || a.numeric_value !== undefined;
    }).length;
  };

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  // Show result screen
  if (result) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="exam-result-screen">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Card className="text-center">
              <CardContent className="pt-8 pb-8 space-y-6">
                <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center ${
                  result.passed ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                }`}>
                  {result.passed ? (
                    <CheckCircle className="w-10 h-10" weight="duotone" />
                  ) : (
                    <XCircle className="w-10 h-10" weight="duotone" />
                  )}
                </div>

                <div>
                  <h1 className="text-2xl font-bold mb-2">
                    {result.passed ? 'Congratulations!' : 'Keep Trying!'}
                  </h1>
                  <p className="text-muted-foreground">
                    {result.passed 
                      ? 'You have successfully passed this exam.' 
                      : 'You did not reach the passing score this time.'}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4 py-4 border-y">
                  <div>
                    <p className="text-3xl font-bold text-primary">{result.percentage?.toFixed(1)}%</p>
                    <p className="text-sm text-muted-foreground">Score</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{result.score}/{result.max_score}</p>
                    <p className="text-sm text-muted-foreground">Points</p>
                  </div>
                  <div>
                    <p className="text-3xl font-bold">{formatTime(result.time_taken_seconds)}</p>
                    <p className="text-sm text-muted-foreground">Time Taken</p>
                  </div>
                </div>

                <Badge variant={result.passed ? 'default' : 'destructive'} className="text-lg py-2 px-4">
                  {result.passed ? 'PASSED' : 'FAILED'}
                </Badge>

                {/* Every attempt at this quiz: how many, when, what was scored */}
                {attemptsApi.attempts.length > 0 && (
                  <div className="text-left mt-6 space-y-2" data-testid="result-attempts">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <h3 className="font-semibold">Your attempts</h3>
                      <span className="text-sm text-muted-foreground">
                        attempt #{result.attempt_number} of {exam?.settings?.max_attempts ?? 1} allowed
                        {attemptsApi.stats.best !== null ? ` · best ${attemptsApi.stats.best.toFixed(0)}%` : ''}
                      </span>
                      <Button variant="outline" size="sm" className="h-7 ml-auto" onClick={() => navigate(`/learn/exams/${examId}/results`, { state: { courseId: location.state?.courseId, attemptId: result.id } })} data-testid="view-all-results">
                        All results
                      </Button>
                    </div>
                    <AttemptsTable attempts={attemptsApi.attempts} selectedId={result.id} passMark={exam?.settings?.passing_score ?? 60} maxAttempts={exam?.settings?.max_attempts} compact />
                  </div>
                )}

                {/* Performance Analysis */}
                {result.analytics && (
                  <div className="mt-6 text-left space-y-4" data-testid="performance-analysis">
                    {/* Interview Readiness */}
                    {result.analytics.interview_readiness && (
                      <Card className={`border-2 ${
                        result.analytics.interview_readiness.status === 'ready' ? 'border-green-300 bg-green-50' :
                        result.analytics.interview_readiness.status === 'needs_practice' ? 'border-amber-300 bg-amber-50' :
                        'border-red-300 bg-red-50'
                      }`}>
                        <CardContent className="p-4 flex items-center justify-between gap-4">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">Interview Readiness</p>
                            <p className={`text-xl font-bold ${
                              result.analytics.interview_readiness.status === 'ready' ? 'text-green-700' :
                              result.analytics.interview_readiness.status === 'needs_practice' ? 'text-amber-700' :
                              'text-red-700'
                            }`}>
                              {result.analytics.interview_readiness.label}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-bold">{result.analytics.interview_readiness.score}%</p>
                            <p className="text-xs text-muted-foreground">Score</p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Topic-wise breakdown */}
                    {result.analytics.topic_performance?.length > 0 && (
                      <Card>
                        <CardHeader className="pb-2"><CardTitle className="text-base">Topic-Wise Performance</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                          {result.analytics.topic_performance.map((tp) => (
                            <div key={tp.topic} className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="font-medium">{tp.topic}</span>
                                <span className="text-muted-foreground">{tp.correct}/{tp.total} · {tp.accuracy}%</span>
                              </div>
                              <Progress value={tp.accuracy} className={`h-2 ${tp.accuracy >= 80 ? '[&>div]:bg-green-500' : tp.accuracy >= 60 ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500'}`} />
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {/* Strengths & Weaknesses */}
                    {(result.analytics.strengths?.length > 0 || result.analytics.weaknesses?.length > 0) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {result.analytics.strengths?.length > 0 && (
                          <Card className="border-green-200 bg-green-50">
                            <CardContent className="p-3">
                              <p className="text-xs font-semibold uppercase text-green-700 mb-1">💪 Strengths</p>
                              <div className="flex flex-wrap gap-1">
                                {result.analytics.strengths.map(s => <Badge key={s} variant="outline" className="border-green-400 text-green-700 bg-white text-xs">{s}</Badge>)}
                              </div>
                            </CardContent>
                          </Card>
                        )}
                        {result.analytics.weaknesses?.length > 0 && (
                          <Card className="border-red-200 bg-red-50">
                            <CardContent className="p-3">
                              <p className="text-xs font-semibold uppercase text-red-700 mb-1">📚 Focus Areas</p>
                              <div className="flex flex-wrap gap-1">
                                {result.analytics.weaknesses.map(w => <Badge key={w} variant="outline" className="border-red-400 text-red-700 bg-white text-xs">{w}</Badge>)}
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Full review: every question with all options, the correct answer and the explanation */}
                {result.answers_detail?.length > 0 && (
                  <div className="text-left mt-6 space-y-3" data-testid="answer-review">
                    <div className="flex items-baseline gap-2">
                      <h3 className="font-semibold">Review your answers</h3>
                      <span className="text-sm text-muted-foreground">
                        {result.answers_detail.filter((a) => a.is_correct).length} of {result.answers_detail.length} correct
                      </span>
                    </div>
                    {result.answers_detail.map((ad, idx) => (
                      <AnswerReview key={ad.question_id || idx} detail={ad} index={idx} />
                    ))}
                  </div>
                )}

                <div className="flex gap-3 justify-center pt-4 flex-wrap">
                  <Button variant="outline" onClick={() => navigate(backTo)}>
                    {location.state?.courseId ? 'Back to course' : 'Back to dashboard'}
                  </Button>
                  <Button variant="outline" onClick={() => navigate(`/learn/exams/${examId}/results`, { state: { courseId: location.state?.courseId, attemptId: result.id } })}>
                    All results
                  </Button>
                  {!result.passed && attemptsApi.stats.count < (exam?.settings?.max_attempts ?? 1) && (
                    <Button onClick={() => window.location.reload()}>
                      Try again ({(exam?.settings?.max_attempts ?? 1) - attemptsApi.stats.count} left)
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse">Loading exam...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (loadError) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <div className="max-w-md w-full rounded-lg border border-amber-200 bg-amber-50 p-6 text-center" data-testid="exam-unavailable">
            <h2 className="font-heading text-lg font-semibold text-amber-900 mb-1">This exam can't be started</h2>
            <p className="text-sm text-amber-800 mb-5">{loadError}</p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={() => navigate(backTo)}>{location.state?.courseId ? 'Back to course' : 'Back to dashboard'}</Button>
              <Button onClick={() => { setLoadError(null); setLoading(true); fetchExam(); startAttempt(); }}>Try again</Button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Leaving the screen ends the session; the attempt is handed in first. Only armed
          while the exam is actually in progress — not on the results screen. */}
      <AwayGuard area="assessment" disabled={!!result} onBeforeSignOut={handInOnSignOut} />
      <div className="p-4 lg:p-6" data-testid="exam-taker-page">
        {/* Header with Timer */}
        {/* top-0: this scrolls inside the shell's content area, which already starts below
            the app header. The old 16 offset left a window of scrolling content above it. */}
        <div className="sticky top-0 z-40 bg-background pb-4">
          <Card>
            <CardContent className="p-4">
              {/* On a phone the title and the controls each get their own line — side by
                  side they squeezed the paper's name down to "Mock…" and wrapped the
                  question count into a three-word column. */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="min-w-0">
                  <h1 className="font-semibold line-clamp-1">{exam?.title}</h1>
                  <p className="text-sm text-muted-foreground">
                    Question {currentIndex + 1} of {questions.length} •
                    {getAnsweredCount()} answered
                  </p>
                </div>

                <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                  {timeRemaining !== null && (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                      timeRemaining < 60 ? 'bg-red-100 text-red-700 animate-pulse' :
                      timeRemaining < 300 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-muted'
                    }`}>
                      <Clock className="w-4 h-4" weight="bold" />
                      <span className="font-mono font-semibold">{formatTime(timeRemaining)}</span>
                    </div>
                  )}
                  
                  <Button variant="outline" size="sm" onClick={() => saveProgress()}>
                    <FloppyDisk className="w-4 h-4 mr-1" />
                    Save
                  </Button>
                  
                  <Button 
                    onClick={() => setShowSubmitDialog(true)}
                    disabled={submitting}
                    data-testid="submit-exam-btn"
                  >
                    <PaperPlaneTilt className="w-4 h-4 mr-1" />
                    Submit
                  </Button>
                </div>
              </div>
              
              <Progress value={progress} className="mt-3 h-2" />
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-4 gap-6 mt-4">
          {/* Question Navigator (Sidebar) */}
          <Card className="lg:col-span-1 h-fit lg:sticky lg:top-36">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ListNumbers className="w-4 h-4" />
                Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-2">
                {questions.map((q, idx) => {
                  const isAnswered = answers[q.id]?.selected_options?.length > 0 || 
                                     answers[q.id]?.answer_text ||
                                     answers[q.id]?.numeric_value !== undefined;
                  const isCurrent = idx === currentIndex;
                  
                  return (
                    <button
                      key={q.id}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                        isCurrent 
                          ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2' 
                          : isAnswered 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-muted hover:bg-muted/80'
                      }`}
                      data-testid={`question-nav-${idx}`}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>
              
              <div className="flex items-center gap-4 mt-4 pt-4 border-t text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-green-100" />
                  <span>Answered</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-muted" />
                  <span>Unanswered</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Question Content */}
          <Card className="lg:col-span-3">
            <CardContent className="p-6">
              <AnimatePresence mode="wait">
                {currentQuestion && (
                  <motion.div
                    key={currentQuestion.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <Badge variant="outline">{currentQuestion.question_type.replace('_', ' ')}</Badge>
                      <Badge variant="secondary">{currentQuestion.points} pts</Badge>
                    </div>

                    <RichHtml html={currentQuestion.question_text} className={`text-lg font-medium ${currentQuestion.media_url ? 'mb-3' : 'mb-6'}`} data-testid="question-text" />
                    {currentQuestion.media_url && (
                      <figure className="mb-6">
                        <img src={currentQuestion.media_url} alt="Figure for this question" className="max-h-72 max-w-full rounded border border-border bg-white object-contain" data-testid="question-figure" />
                      </figure>
                    )}

                    {/* MCQ Single */}
                    {currentQuestion.question_type === 'MCQ_SINGLE' && (
                      <RadioGroup
                        value={answers[currentQuestion.id]?.selected_options?.[0] || ''}
                        onValueChange={(value) => updateAnswer(currentQuestion.id, { selected_options: [value] })}
                        className="space-y-3"
                      >
                        {currentQuestion.options?.map((opt) => (
                          <div key={opt.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer" data-testid={`option-${opt.id}`}>
                            <RadioGroupItem value={opt.id} id={opt.id} />
                            <Label htmlFor={opt.id} className="flex-1 cursor-pointer"><RichHtml html={opt.text} as="span" /></Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}

                    {/* MCQ Multi */}
                    {currentQuestion.question_type === 'MCQ_MULTI' && (
                      <div className="space-y-3">
                        {currentQuestion.options?.map((opt) => {
                          const isChecked = answers[currentQuestion.id]?.selected_options?.includes(opt.id);
                          return (
                            <div key={opt.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50" data-testid={`option-${opt.id}`}>
                              <Checkbox
                                id={opt.id}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  const current = answers[currentQuestion.id]?.selected_options || [];
                                  const newSelected = checked 
                                    ? [...current, opt.id]
                                    : current.filter(id => id !== opt.id);
                                  updateAnswer(currentQuestion.id, { selected_options: newSelected });
                                }}
                              />
                              <Label htmlFor={opt.id} className="flex-1 cursor-pointer"><RichHtml html={opt.text} as="span" /></Label>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* True/False */}
                    {currentQuestion.question_type === 'TRUE_FALSE' && (
                      <RadioGroup
                        value={answers[currentQuestion.id]?.selected_options?.[0] || ''}
                        onValueChange={(value) => updateAnswer(currentQuestion.id, { selected_options: [value] })}
                        className="space-y-3"
                      >
                        {currentQuestion.options?.map((opt) => (
                          <div key={opt.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer" data-testid={`option-${opt.id}`}>
                            <RadioGroupItem value={opt.id} id={opt.id} />
                            <Label htmlFor={opt.id} className="flex-1 cursor-pointer"><RichHtml html={opt.text} as="span" /></Label>
                          </div>
                        ))}
                      </RadioGroup>
                    )}

                    {/* Fill in the Blank / Short Answer */}
                    {['FILL_BLANK', 'SHORT_ANSWER'].includes(currentQuestion.question_type) && (
                      <Input
                        value={answers[currentQuestion.id]?.answer_text || ''}
                        onChange={(e) => updateAnswer(currentQuestion.id, { answer_text: e.target.value })}
                        placeholder="Type your answer..."
                        className="max-w-md"
                        data-testid="text-answer-input"
                      />
                    )}

                    {/* Essay */}
                    {currentQuestion.question_type === 'ESSAY' && (
                      <Textarea
                        value={answers[currentQuestion.id]?.answer_text || ''}
                        onChange={(e) => updateAnswer(currentQuestion.id, { answer_text: e.target.value })}
                        placeholder="Write your essay answer..."
                        rows={8}
                        data-testid="essay-answer-input"
                      />
                    )}

                    {/* Numeric */}
                    {currentQuestion.question_type === 'NUMERIC' && (
                      <Input
                        type="number"
                        step="any"
                        value={answers[currentQuestion.id]?.numeric_value ?? ''}
                        onChange={(e) => updateAnswer(currentQuestion.id, { 
                          numeric_value: e.target.value ? parseFloat(e.target.value) : undefined 
                        })}
                        placeholder="Enter a number..."
                        className="max-w-xs"
                        data-testid="numeric-answer-input"
                      />
                    )}

                    {/* Matching */}
                    {currentQuestion.question_type === 'MATCH' && (
                      <div className="space-y-3" data-testid="matching-question">
                        <p className="text-sm text-muted-foreground mb-2">Match each item on the left with its pair on the right:</p>
                        {currentQuestion.options?.map((opt) => (
                          <div key={opt.id} className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
                            <span className="font-medium flex-1">{opt.text}</span>
                            <span className="text-muted-foreground mx-2">&rarr;</span>
                            <select
                              className="flex-1 h-9 px-3 border rounded-md bg-background text-sm"
                              value={answers[currentQuestion.id]?.match_pairs?.[opt.id] || ''}
                              onChange={(e) => {
                                const currentPairs = answers[currentQuestion.id]?.match_pairs || {};
                                updateAnswer(currentQuestion.id, { 
                                  match_pairs: { ...currentPairs, [opt.id]: e.target.value } 
                                });
                              }}
                            >
                              <option value="">Select match...</option>
                              {currentQuestion.options?.map((rightOpt) => (
                                <option key={rightOpt.id} value={rightOpt.match_right || rightOpt.text}>
                                  {rightOpt.match_right || rightOpt.text}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Ordering */}
                    {currentQuestion.question_type === 'ORDER' && (
                      <div className="space-y-2" data-testid="ordering-question">
                        <p className="text-sm text-muted-foreground mb-2">Drag items to arrange in the correct order (use arrows):</p>
                        {(() => {
                          const orderedItems = answers[currentQuestion.id]?.order_items || 
                            currentQuestion.options?.map(o => o.id) || [];
                          const optionMap = {};
                          currentQuestion.options?.forEach(o => { optionMap[o.id] = o; });
                          
                          return orderedItems.map((itemId, idx) => {
                            const opt = optionMap[itemId];
                            if (!opt) return null;
                            return (
                              <div key={itemId} className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                                <span className="text-sm font-mono text-muted-foreground w-6">{idx + 1}.</span>
                                <span className="flex-1 font-medium">{opt.text}</span>
                                <Button 
                                  variant="ghost" size="sm" className="h-7 w-7 p-0"
                                  disabled={idx === 0}
                                  onClick={() => {
                                    const items = [...orderedItems];
                                    [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
                                    updateAnswer(currentQuestion.id, { order_items: items });
                                  }}
                                >
                                  &uarr;
                                </Button>
                                <Button
                                  variant="ghost" size="sm" className="h-7 w-7 p-0"
                                  disabled={idx === orderedItems.length - 1}
                                  onClick={() => {
                                    const items = [...orderedItems];
                                    [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
                                    updateAnswer(currentQuestion.id, { order_items: items });
                                  }}
                                >
                                  &darr;
                                </Button>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}

                    {/* File Upload */}
                    {currentQuestion.question_type === 'FILE_UPLOAD' && (
                      <div className="space-y-3" data-testid="file-upload-question">
                        {answers[currentQuestion.id]?.file_name ? (
                          <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
                            <span className="flex-1 text-sm font-medium">{answers[currentQuestion.id].file_name}</span>
                            <Button variant="outline" size="sm" onClick={() => updateAnswer(currentQuestion.id, { file_name: null, file_url: null })}>
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <label className="block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors">
                            <input
                              type="file"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  const res = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                                  updateAnswer(currentQuestion.id, { file_url: res.data.path, file_name: file.name });
                                  toast.success('File uploaded');
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to upload file');
                                }
                              }}
                            />
                            <p className="text-sm text-muted-foreground">Click to upload a file</p>
                          </label>
                        )}
                      </div>
                    )}

                    {/* Rating */}
                    {currentQuestion.question_type === 'RATING' && (
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            onClick={() => updateAnswer(currentQuestion.id, { rating })}
                            className={`w-12 h-12 rounded-lg text-lg font-semibold transition-all ${
                              answers[currentQuestion.id]?.rating === rating
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted hover:bg-muted/80'
                            }`}
                          >
                            {rating}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Text Feedback */}
                    {currentQuestion.question_type === 'TEXT_FEEDBACK' && (
                      <Textarea
                        value={answers[currentQuestion.id]?.answer_text || ''}
                        onChange={(e) => updateAnswer(currentQuestion.id, { answer_text: e.target.value })}
                        placeholder="Enter your feedback..."
                        rows={4}
                      />
                    )}

                    {/* Hint */}
                    {currentQuestion.hint && (
                      <p className="mt-4 text-sm text-muted-foreground italic">
                        Hint: {currentQuestion.hint}
                      </p>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex justify-between mt-8 pt-6 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
                        disabled={currentIndex === 0}
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Previous
                      </Button>
                      
                      {currentIndex === questions.length - 1 ? (
                        <Button onClick={() => setShowSubmitDialog(true)}>
                          <PaperPlaneTilt className="w-4 h-4 mr-2" />
                          Review & Submit
                        </Button>
                      ) : (
                        <Button onClick={() => setCurrentIndex(prev => Math.min(questions.length - 1, prev + 1))}>
                          Next
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Exam?</AlertDialogTitle>
            <AlertDialogDescription>
              You have answered {getAnsweredCount()} out of {questions.length} questions.
              {getAnsweredCount() < questions.length && (
                <span className="block mt-2 text-yellow-600">
                  <Warning className="inline w-4 h-4 mr-1" />
                  You have {questions.length - getAnsweredCount()} unanswered questions.
                </span>
              )}
              <br /><br />
              Once submitted, you cannot change your answers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Exam</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Exam'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
